import {
  Visitor,
  VisitorUpdates,
  VisitorHelpStatus,
  SESSION_ACTION,
  VisitorSession,
} from 'sb-lib/types';
import { getMySbid } from 'shared/utils/helpers';
import { getVisitorDisplayName } from 'shared/utils/visitor';
import { ActiveVisitor, Client, ConsolidatedSession, PageVisit, SelfUser } from 'types';
import type { AppState } from './reducers';
import type { DashboardState, Filter } from './dashboard-slice';

const SERVICEBELL_ORG_ID = 770540926; // production
const NO_AGENT = 0;

// Keep me in sync with application/realtime2/store/visitor_data.py sort_visitor_data
export const sortClientList = (clientList: Client[]) => {
  const copy = [...clientList].map(client => {
    return {
      ...client,
      // TODO: Move adding this property to somewhere that makes more sense?
      displayName: getVisitorDisplayName(client) || 'Unknown',
    };
  });
  const statusOrder = [VisitorHelpStatus.ALERT, VisitorHelpStatus.WAITING];
  copy.sort((c1, c2) => {
    // Sort visitors with an agent at the top
    if (c1.helpedBy !== NO_AGENT || c2.helpedBy !== NO_AGENT) {
      return c2.helpedBy - c1.helpedBy;
    }

    // Sort waiting next, followed by alerts
    const statusIdx1 = statusOrder.indexOf(c1.helpStatus);
    const statusIdx2 = statusOrder.indexOf(c2.helpStatus);
    if (statusIdx1 !== statusIdx2) {
      return statusIdx2 - statusIdx1;
    }
    // Sort page not visible to the bottom
    if (c1.isPageVisible !== c2.isPageVisible) {
      return c1.isPageVisible ? -1 : 1;
    }
    // All else being equal, sort by longest connection
    return c1.created - c2.created;
  });
  return copy;
};

export const filterClientList = (clientList: Client[], filter: Filter) => {
  switch (filter.type) {
    case 'name':
      return clientList.filter(v => v.displayName === filter.value);
    case 'customId':
      return clientList.filter(v => v.customId === filter.value);
    case 'email':
      return clientList.filter(v => v.email === filter.value);
    case 'path': {
      return clientList.filter(v => {
        try {
          const url = new URL(v.url);
          return url.pathname === filter.value;
        } catch (err) {
          return false;
        }
      });
    }
  }
};

const isSelfClient = (client: Client, authed: SelfUser | null, selfSbid?: string) => {
  const isSelfSbid = client.sbid === selfSbid;
  const isClientServicebellSelf =
    client.orgId === SERVICEBELL_ORG_ID && client.customId === String(authed?.id);
  return isSelfSbid || isClientServicebellSelf;
};

export const sortAndFilterSelfFromClientList = (
  clientList: Client[],
  authedUser: SelfUser | null,
) => {
  // sort & filter out self (for app.getservicebell org users)
  const selfSbid = getMySbid();
  return sortClientList(clientList).filter(c => {
    return !isSelfClient(c, authedUser, selfSbid);
  });
};

export const consolidateSessions = (state: DashboardState) => {
  const consolidated = [] as ConsolidatedSession[];
  const makeNewConSess = () =>
    ({
      id: '',
      startTime: 0,
      endTime: 0,
      duration: 0,
      referrer: '',
      activities: [],
      pageVisits: [],
      isFirst: false,
      user: null,
    } as ConsolidatedSession);
  let cur = makeNewConSess();
  const staleAfter = 1000 * 60 * 5;

  const addSessToConSess = (cur: ConsolidatedSession, sess: VisitorSession) => {
    cur.id = cur.id || sess.id;
    cur.endTime = cur.endTime || sess.endTime;
    cur.startTime = sess.startTime || cur.startTime;
    cur.duration += sess.endTime ? sess.endTime - sess.startTime : 0;
    cur.referrer = cur.referrer || sess.referrer;
    cur.pageVisits = cur.pageVisits.concat(
      sess.sessionActions
        .filter(x => x.action === SESSION_ACTION.PAGE_VISIT && !!x.data)
        .map(pv => ({
          url: pv.data?.url || '',
          urlTitle: pv.data?.urlTitle || '',
          duration: 0,
          timestamp: pv.timestamp,
        })),
    );
    cur.activities = cur.activities.concat([
      ...sess.sessionActions
        .map(x => x.action)
        .filter(x => x !== SESSION_ACTION.PAGE_VISIT),
      ...sess.sessionInteractions.map(x => x.kind),
    ]);
    cur.activities = Array.from(new Set(cur.activities));
    cur.activities.sort((a, b) => (a < b ? 1 : -1));
    cur.user = sess.sessionInteractions.find(si => si.user)?.user || cur.user;
  };

  // expect sessions to be ordered by date (newest to oldest)
  state.activeVisitorSessions.pages.forEach(page => {
    page.forEach(sess => {
      if (cur.startTime && cur.startTime - sess.endTime > staleAfter) {
        consolidated.push(cur);
        cur = makeNewConSess();
      }
      addSessToConSess(cur, sess);
    });
  });
  consolidated.push(cur);

  // If we're paginated, hook up the first session in the list at the end
  const first = state.activeVisitorFirstSession;
  if (state.activeVisitorSessions.hasMore && first) {
    cur = makeNewConSess();
    addSessToConSess(cur, first);
    consolidated.push(cur);
  }

  if (consolidated.length) {
    consolidated[consolidated.length - 1].isFirst = true;
  }

  // consolidate & fill in page durations
  consolidated.forEach(cs => {
    cs.pageVisits.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));
    const consolidatedPageVisits = cs.pageVisits.reduce<PageVisit[]>((acc, pv, i) => {
      const lastPv = acc[acc.length - 1];
      // fill in duration
      if (lastPv) {
        lastPv.duration = pv.timestamp - lastPv.timestamp;
      }
      // add first & new urls only
      if (!lastPv || (lastPv && lastPv.url !== pv.url)) {
        acc.push(pv);
      }
      if (i === cs.pageVisits.length - 1) {
        pv.duration = cs.endTime - pv.timestamp;
        // if negative endTime, just take the diff from now
        pv.duration = pv.duration > 0 ? pv.duration : Date.now() - pv.timestamp;
      }
      return acc;
    }, []);
    cs.pageVisits = consolidatedPageVisits;
  });
  state.activeVisitorConsolidatedSessions = consolidated;
};

export const detectAssociatedVisitorIdChange = (
  state: AppState,
  updates: VisitorUpdates,
) => {
  const selfSid = state.realtime.sid;
  const { intendedVisitorId } = state.realtime;
  if (!intendedVisitorId) {
    return null;
  }
  const intendedVisitor = state.realtime.visitorMap[intendedVisitorId];
  if (!intendedVisitor) {
    return null;
  }
  if (selfSid) {
    const found = Object.entries(updates).find(([_, upd]) => {
      if (upd.type === 'UPDATE' && intendedVisitorId) {
        const vis = upd.data;
        const isSameSidAsAssociated = intendedVisitor.sid === vis.sid;
        const isMySidHelping = selfSid && vis.helpedBySid && selfSid === vis.helpedBySid;
        const isIdDiff = intendedVisitorId !== vis.sbid;
        return isSameSidAsAssociated && isMySidHelping && isIdDiff;
      }
    });
    return (found && found[1].data) || null;
  }
  return null;
};

export const makeDefaultVisitor = (): Visitor => {
  return {
    sid: '',
    sbid: '',
    replacedSbid: '',
    orgId: 0,
    location: '',
    url: '',
    urlTitle: '',
    displayName: '',
    email: '',
    customId: undefined,
    customMetadata: undefined,
    helpStatus: VisitorHelpStatus.IDLE,
    helpStatusUpdated: 0,
    helpedBy: 0,
    helpedBySid: '',
    helpedByName: '',
    helpedByTitle: '',
    helpedByAvatar: '',
    helpedByConnectionType: null,
    title: '',
    isPageVisible: true,
    isSpying: false,
    isTakeover: false,
    isVideo: false,
    callConversationId: 0,
    isTakeoverAccepted: false,
    isCallWaitingOnVisitor: false,
    isDisplaydemo: false,
    isDisplayshare: false,
    isDisplayshareAccepted: false,
    isSpyingAccepted: false,
    isSpyingRejected: false,
    isVideoAccepted: false,
    isVisitorRequestingVideo: false,
    isVisitorVideoAllowed: true,
    hasAlerted: false,
    hasDialed: false,
    isRecording: false,
    isRecordingAccepted: false,
    isRecordingRejected: false,
    wantsChat: false,
    created: 0,
    updated: 0,
    streams: [],
    routedUserIds: [],
    sixsenseRevealData: undefined,
    clearbitRevealData: undefined,
  };
};

export const makeFakeTourClient = (): Client => {
  // Need to define all of these keys despite most being falsy.
  return {
    ...makeDefaultVisitor(),
    // Spoofed fields
    created: Date.now(),
    updated: Date.now(),
    orgId: 0,
    sid: 'gHKhQ4pgegs-tour-example',
    sbid: 'b4a16207-tour-example',
    location: 'Minneapolis, MN',
    url: 'https://servicebell.com',
    urlTitle: 'Rep.ai - Homepage',
    helpStatus: VisitorHelpStatus.IDLE,
    helpStatusUpdated: Date.now(),
    displayName: 'Daniel Ternyak',
    customId: 'tour-example',
    customMetadata: {
      displayName: 'Daniel Ternyak',
      email: 'hello@servicebell.com',
      avatar:
        'https://uploads.servicebell.com/cdn-cgi/image/width=80,height=80,f=auto/avatars/1109432277.0baaebfe409840bcb7fceeea54c256fe.png',
    },
  };
};

export const makeFakeTourActiveVisitor = (): ActiveVisitor => {
  const client = makeFakeTourClient();
  return {
    ...client,
    id: client.sbid,
    isLoading: false,
    hasLoaded: true,
    isEditing: false,
    contact: null,
    company: null,
  };
};

export const makeDissociatedVisitor = (visitor: Visitor) => {
  return {
    ...visitor,
    ...{
      helpedBy: 0,
      helpedBySid: '',
      helpedByName: '',
      helpedByTitle: '',
      helpedByAvatar: '',
      isSpying: false,
      isTakeover: false,
      isTakeoverAccepted: false,
      isVideo: false,
      isVideoAccepted: false,
    },
  } as Visitor;
};
